/**
 * Preferences (settings pocket spec §6): the registry's preference pages for
 * this platform, each with a short summary on the right. Pages remount on
 * navigation, so the summaries are read fresh whenever the list shows again.
 */
import { useEffect, useState } from "react";
import { PocketRow, PocketSection } from "@/components/ui";
import { AppLanguagePicker } from "@/components/language/app-language-picker";
import { useT, type Translator } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import {
  settingsPreferencesMessages,
  SOUND_LABEL_KEY,
  THEME_LABEL_KEY,
} from "@/i18n/messages/settings-preferences";
import { settingsShellMessages } from "@/i18n/messages/settings-shell";
import { usePrivacyConsent } from "@/lib/privacy-consent";
import { useAuth } from "@/hooks/use-auth";
import { getSoundType, isSoundEnabled } from "@/lib/notification-sound";
import { getStoredThemeChoice } from "@/lib/theme";
import { getStoredTransparency } from "@/lib/transparency";
import { useTtsSettingsStore } from "@/stores/tts-settings-store";
import { useUsageDataStore } from "@/stores/usage-data-store";
import {
  DENKER_AGENT_VOICES,
  windowsSystemVoices,
} from "@/desktop/denker-agent-voice";
import { getInputConsent, inputConsentApplies } from "@/desktop/input-consent";
import {
  getPointerControlStatus,
  pointerControlApplies,
  type PointerControlPolicy,
} from "@/desktop/pointer-control-policy";

import { useUpdateSettings } from "@/desktop/update-settings";
import { useVoiceEngineChoice } from "@/desktop/use-voice-engine-choice";
import { attentionStatus, useRuntimeAttentionCount } from "./connections-state";
import {
  currentSettingsPlatform,
  SETTINGS_PAGES,
  useSettingsNav,
  type SettingsPageId,
} from "./settings-navigation";

type PreferencesT = Translator<typeof settingsPreferencesMessages.en>;
type CommonT = Translator<typeof commonMessages.en>;

function useVoiceSummary(t: PreferencesT, tCommon: CommonT): string {
  const enabled = useTtsSettingsStore((s) => s.enabled);
  const denkerVoiceId = useTtsSettingsStore((s) => s.denkerVoiceId);
  const windowsVoiceId = useTtsSettingsStore((s) => s.windowsVoiceId);
  const { engine, isSelectable } = useVoiceEngineChoice();
  if (!enabled) return tCommon("off");
  const name =
    engine === "system" && isSelectable
      ? windowsSystemVoices().find((voice) => voice.id === windowsVoiceId)?.name
      : DENKER_AGENT_VOICES.find((voice) => voice.id === denkerVoiceId)?.name;
  return t("voiceSummary", { name: name ?? t("voiceDefaultName") });
}

/** Device extras win; otherwise the account's usage data choice once read. */
function usePrivacySummary(t: PreferencesT): string | undefined {
  const { analyticsSetting, diagnostics } = usePrivacyConsent();
  const usageData = useUsageDataStore((s) => s.enabled);
  const loadUsageData = useUsageDataStore((s) => s.load);
  useEffect(() => {
    void loadUsageData();
  }, [loadUsageData]);
  if (analyticsSetting || diagnostics) return t("privacySharing");
  if (usageData === null) return undefined;
  return t(usageData ? "privacyUsageOnly" : "privacyOff");
}

const POINTER_SUMMARY_KEY = {
  ask_every_task: "pointerSummaryAsk",
  always_allow: "policyAlways",
  never_allow: "policyNever",
} as const satisfies Record<
  PointerControlPolicy,
  keyof typeof settingsPreferencesMessages.en
>;

type ScreenSummary =
  | { policy: PointerControlPolicy }
  | { granted: boolean }
  | undefined;

function useScreenSummary(): ScreenSummary {
  const [summary, setSummary] = useState<ScreenSummary>();
  useEffect(() => {
    let active = true;
    if (pointerControlApplies()) {
      getPointerControlStatus().then(
        (status) => {
          if (active) setSummary({ policy: status.policy });
        },
        () => {},
      );
    } else if (inputConsentApplies()) {
      void getInputConsent().then((granted) => {
        if (active) setSummary({ granted });
      });
    }
    return () => {
      active = false;
    };
  }, []);
  return summary;
}

export function PreferencesPage() {
  const nav = useSettingsNav();
  const t = useT(settingsPreferencesMessages);
  const tCommon = useT(commonMessages);
  // Page titles come from the shell catalogue; this re-renders them.
  useT(settingsShellMessages);
  const privacy = usePrivacySummary(t);
  const { user } = useAuth();
  const autoUpdate = useUpdateSettings((s) => s.autoUpdate);
  const runtimeAttention = useRuntimeAttentionCount();
  const voice = useVoiceSummary(t, tCommon);
  const screen = useScreenSummary();
  const [theme] = useState(getStoredThemeChoice);
  const [transparency] = useState(getStoredTransparency);
  const [sound] = useState(() => (isSoundEnabled() ? getSoundType() : null));
  const appearance = {
    theme: t(THEME_LABEL_KEY[theme]),
    avatars: t(
      (user?.agent_avatar_style || "glass") === "icons"
        ? "avatarsSummaryIcons"
        : "avatarsSummaryGlass",
    ),
  };

  const summaries: Partial<Record<SettingsPageId, string>> = {
    "preferences-appearance":
      transparency < 100
        ? t("appearanceSummarySolid", {
            ...appearance,
            percent: 100 - transparency,
          })
        : t("appearanceSummary", appearance),
    "preferences-voice": voice,
    "preferences-sounds": sound ? t(SOUND_LABEL_KEY[sound]) : tCommon("off"),
    "preferences-screen": !screen
      ? undefined
      : "policy" in screen
        ? t(POINTER_SUMMARY_KEY[screen.policy])
        : tCommon(screen.granted ? "on" : "off"),
    "preferences-updates": t(autoUpdate ? "updatesAutomatic" : "updatesManual"),
    "preferences-privacy": privacy,
  };
  const platform = currentSettingsPlatform();
  const pages = Object.values(SETTINGS_PAGES).filter(
    (def) =>
      def.parent === "preferences" &&
      (!def.platforms || def.platforms.includes(platform)),
  );

  return (
    <PocketSection testId="settings-page-preferences">
      <PocketRow
        testId="settings-row-app-language"
        title={tCommon("language")}
        trailing={<AppLanguagePicker />}
      />
      {pages.map((def) => (
        <PocketRow
          key={def.id}
          testId={`settings-row-${def.id}`}
          title={def.title}
          trailing={summaries[def.id]}
          status={
            def.id === "preferences-ai-tools"
              ? attentionStatus(runtimeAttention)
              : undefined
          }
          chevron
          onSelect={() => nav.push({ page: def.id })}
        />
      ))}
    </PocketSection>
  );
}
